import {accountsByPk} from './wizardPayload';
import {REASON_DORMANT, REASON_NO_RECENT_SPEND, REASON_PLATFORM_DISABLED} from './wizardReducer';

/**
 * De decisiones sueltas por cuenta a las filas que el usuario ve: un cliente con sus cuentas.
 *
 * La pantalla habla de clientes y el estado guarda cuentas, porque una cuenta puede cambiar de
 * cliente y un cliente puede no existir todavía. Esta es la traducción, y vive aparte de los
 * componentes para poder probarla: agrupar mal es la clase de error que se ve tarde, cuando ya se
 * creó la cartera.
 */

export const KEY_UNASSIGNED = '__unassigned__';

export function clientGroupsOf(state, candidates) {
    const byPk = accountsByPk(candidates);
    const groups = new Map();
    const removed = [];

    Object.keys(state.decisions.byAccount).forEach(pk => {
        const decision = state.decisions.byAccount[pk];
        const account = byPk[pk];
        if (!account) return;

        // Lo que el usuario sacó no es una fila apagada dentro de su cliente: es un cajón aparte.
        // Se guarda de dónde salió para poder nombrarlo al ofrecer el deshacer — «Rayovac» dice
        // mucho más que «una cuenta».
        if (decision.dismissed) {
            removed.push({pk, account, decision, clientName: displayNameOf(decision, state)});
            return;
        }

        const key = groupKeyOf(decision);
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                clientRef: decision.clientRef,
                name: displayNameOf(decision, state),
                // El nombre con el que se ORDENA: el original, no el renombrado. Ordenar por el
                // que se ve hacía que renombrar una fila la mandara a otra letra de la lista, bajo
                // el cursor del usuario (card 03NF0The). Renombrar es corregir un rótulo, no mover
                // una fila.
                sortName: decision.clientRef?.type === 'new' ? decision.clientRef.name : decision.proposedName,
                isNew: decision.clientRef?.type === 'new',
                confidence: decision.confidence,
                accounts: [],
                doubts: [],
            });
        }
        const group = groups.get(key);
        const entry = {pk, account, decision};
        // La duda vive como una fila hija bajo su cliente: preguntar en el lugar donde está la
        // respuesta es lo que evita que el usuario tenga que buscar de qué se le está hablando.
        if (decision.isDoubt && decision.doubtAnswer === undefined) group.doubts.push(entry);
        else group.accounts.push(entry);
    });

    const all = Array.from(groups.values());
    return {
        assigned: all.filter(group => group.key !== KEY_UNASSIGNED).sort(byName),
        unassigned: all.find(group => group.key === KEY_UNASSIGNED)?.accounts || [],
        removed,
    };
}

function groupKeyOf(decision) {
    if (!decision.clientRef) return KEY_UNASSIGNED;
    if (decision.clientRef.type === 'existing') return `existing:${decision.clientRef.id}`;
    return `new:${decision.clientRef.name}`;
}

function displayNameOf(decision, state) {
    if (!decision.clientRef) return null;
    if (decision.clientRef.type === 'existing') return decision.proposedName;
    return state.decisions.clientRenames[decision.clientRef.name] || decision.clientRef.name;
}

function byName(a, b) {
    return (a.name || '').localeCompare(b.name || '');
}

/**
 * Los clientes que realmente se van a crear: los que llegan con al menos una cuenta tildada.
 *
 * NO es lo mismo que `groups.assigned`. Un grupo entero destildado sigue siendo un grupo —la fila
 * queda en pantalla, tachada— pero no produce ningún cliente ni ninguna línea, y los grupos de 2+
 * cuentas nacen destildados por diseño, así que son la mayoría al cargar la propuesta. Contar
 * grupos hacía que el paso 3 prometiera «3 clientes» y el paso 4 mostrara 1.
 *
 * Devuelve los grupos ya recortados a sus cuentas incluidas, que es la forma en que los necesita el
 * paso de presupuestos: una sola definición para los dos pasos, en vez de dos que pueden divergir.
 */
export function includedGroupsOf(assignedGroups) {
    return assignedGroups
        .map(group => ({...group, accounts: entriesOf(group).filter(entry => entry.decision.include),
                        // Ya quedaron adentro de `accounts`: dejarlas también acá las contaría dos
                        // veces en cualquier consumidor que vuelva a concatenar.
                        doubts: []}))
        .filter(group => group.accounts.length > 0);
}

/**
 * ¿Esta fila va a producir un cliente?
 *
 * Es la MISMA pregunta que contesta `includedGroupsOf` para toda la lista, y vive acá suelta porque
 * la barra de filtros del paso 3 necesita hacérsela fila por fila. Con dos definiciones, un filtro
 * de «listas para crear» podía mostrar un conjunto y el botón «Continuar» contar otro — que es
 * exactamente la clase de divergencia que el usuario descubre en el paso siguiente.
 */
export function groupIsIncluded(group) {
    return entriesOf(group).some(entry => entry.decision.include);
}

/**
 * Las entradas de una fila: sus cuentas MÁS las que están en duda.
 *
 * Vive acá y se exporta porque es la unidad de todo lo que la fila hace —contar, tildar, apagar,
 * mandar al payload— y tenerla escrita en cada lugar fue exactamente lo que se desincronizó.
 *
 * Las dudas cuentan como cualquier otra cuenta porque **el payload las manda igual**:
 * `wizardPayload` filtra por `include` y `clientRef`, nunca por `isDoubt`. Una duda tildada crea
 * su línea, así que dejarla afuera de la cuenta hace que el botón prometa menos de lo que el POST
 * lleva. Y no es un caso de borde: una fila cuyas entradas están TODAS en duda tiene `accounts`
 * vacío, y en una cartera dominada por merges eso es la mayoría de la pantalla.
 */
export function entriesOf(group) {
    return (group?.accounts || []).concat(group?.doubts || []);
}

/**
 * Un grupo que junta varias cuentas — la definición ÚNICA.
 *
 * Existía dos veces con dos criterios distintos, y las dos se dibujaban a 55 px de distancia en el
 * paso 3: el toggle contaba `accounts >= 2` sobre la propuesta del SERVIDOR (fija) y el renglón del
 * tilde maestro `accounts + doubts > 1` sobre la lista VIVA. En producción eso daba «Agrupadas como
 * propone Dashbo (25 con varias cuentas)» arriba de «27 agrupan varias cuentas: conviene
 * revisarlas» — para el lector, un subconjunto más grande que su total.
 *
 * Cuenta las dudas porque una duda sin responder sigue siendo una cuenta dentro del grupo: es
 * exactamente lo que hay que revisar.
 */
export function isMultiAccountGroup(group) {
    return entriesOf(group).length > 1;
}

/**
 * Cuántas cuentas van a quedar en manos del usuario.
 *
 * Se muestra en pantalla a propósito, y no como un caso de borde escondido: la agrupación
 * automática acierta poco, así que la parte manual es una etapa esperada del recorrido. Decir "16
 * de 19 las agrupamos nosotros" convierte lo que falta en un resto manejable en vez de en la
 * sensación de que el sistema falló.
 */
/**
 * Las cuentas dormidas que viajan en el cajón de las descartadas.
 *
 * Existen porque una dormida que YA tiene cliente no puede ir a `unassigned` —ahí no hay dónde
 * guardar su dueño, y perderlo obligaría a reasignarla a mano para recuperarla—, así que baja al
 * cajón por `removed` con su motivo. Para el usuario son lo mismo que las otras dormidas, y
 * cualquier número que hable de «lo que falta revisar» tiene que incluirlas.
 */
export function dormantIn(entries) {
    return (entries || []).filter(entry => entry.decision?.reason === REASON_DORMANT);
}

/** ¿La plataforma reporta cerrada esta cuenta? Es lo único del cajón que no pide ninguna decisión. */
export function isClosedEntry(entry) {
    return entry?.decision?.reason === REASON_PLATFORM_DISABLED;
}

/**
 * ¿Esta cuenta del cajón la sacó el usuario de un cliente, y todavía espera dueño?
 *
 * La marca es `unassignedFrom` y no el `reason`: descartar la cuenta y recuperarla borra el motivo
 * (`dismissChange`), y tiene que seguir diciendo de qué cliente salió.
 */
export function isUnassignedByUser(entry) {
    return !entry?.decision?.clientRef && Boolean(entry?.decision?.unassignedFrom);
}

/**
 * Cómo se reparte el cajón: lo que pide revisión y lo que la plataforma reporta cerrado.
 *
 * Es la definición ÚNICA de los números que la pantalla repite en cuatro lugares —el subtítulo, el
 * chip de arriba, el título del cajón y el filtro «Fuera de la cartera»—, y la que hace que se
 * reconcilien a la vista: `agrupadas + review + closed` es el total de cuentas, pase lo que pase.
 *
 * **Lo borrado con el tacho cuenta igual que lo sacado a mano** (card 2yINyKQv). «Sacar de este
 * cliente» sumaba al «para revisar» y el tacho no, así que en la pantalla de Leo el cajón decía
 * «Fuera de la cartera (998)» sobre «26 para revisar + 967 cerradas»: 5 cuentas que no estaban en
 * ningún número. Son el mismo gesto —el usuario sacó cuentas de la cartera— y viven en la misma
 * sección del cajón, así que van al mismo contador.
 */
export function bucketCountsOf(groups) {
    const removed = groups?.removed || [];
    const closed = removed.filter(isClosedEntry).length;
    return {review: (groups?.unassigned || []).length + removed.length - closed, closed};
}

/**
 * Por qué quedó afuera cada cuenta del cajón, contado por motivo: lo que nombra el resumen del paso 3.
 *
 * El resumen decía «220 necesitan revisión» sobre cuentas que conviene ignorar (H-04, recorrido con
 * Pablo del 13/10). Ahora dice cuántas quedan afuera y por qué, y el motivo tiene que ser cierto
 * para las que nombra: en una agencia nueva casi ninguna cuenta tiene catálogo, así que el cajón
 * se llena de cuentas cuyo nombre no se pudo agrupar, no de cuentas sin campañas activas. Por eso
 * se cuenta cada motivo y la frase nombra sólo los que hay.
 *
 * Cada cuenta va a UN solo motivo, en este orden: cerrada, sin inversión en la semana (C-02: la lista
 * `no_recent_spend` del backend), sin campañas activas, sacada por el usuario, sin un cliente claro.
 * `total` es el mismo número que `bucketCountsOf` (review + closed).
 */
export function outReasonsOf(groups) {
    const counts = {total: 0, closed: 0, noSpend: 0, dormant: 0, taken: 0, unclear: 0};
    // Lo que está en `removed` y no es cerrada ni dormida lo sacó el usuario: el sistema sólo
    // descarta esas dos (ver `dismissedAtOf`).
    const count = (entry, isRemoved) => {
        counts.total += 1;
        if (isClosedEntry(entry)) counts.closed += 1;
        else if (entry.decision?.reason === REASON_NO_RECENT_SPEND) counts.noSpend += 1;
        else if (entry.decision?.reason === REASON_DORMANT) counts.dormant += 1;
        else if (isRemoved || isUnassignedByUser(entry)) counts.taken += 1;
        else counts.unclear += 1;
    };
    (groups?.unassigned || []).forEach(entry => count(entry, false));
    (groups?.removed || []).forEach(entry => count(entry, true));
    return counts;
}

export function manualWorkOf(groups) {
    return {
        automatic: groups.assigned.length,
        // Todo lo del cajón que no está cerrado: lo que espera dueño, las dormidas (también las que
        // bajan por `removed` porque conservan su cliente) y lo que el usuario sacó. Es el MISMO
        // número que el título del cajón: en la agencia 130 llegaron a decir 218 y 231 en la misma
        // pantalla, cada uno contando lo suyo (ver `bucketCountsOf`).
        pending: bucketCountsOf(groups).review,
        dismissed: (groups.removed || []).length,
        doubts: groups.assigned.reduce((total, group) => total + group.doubts.length, 0),
    };
}

/**
 * Lo que una persona sacó DESDE un paso, agrupado por cliente y listo para el deshacer.
 *
 * Sale de las decisiones persistidas y no de un estado del componente: el cajón de deshacer del paso
 * 4 vivía en un `useState` y se perdía en cualquier remonte —una recarga, o volver al paso 3 y
 * avanzar—, mientras el descarte sí sobrevivía. El usuario quedaba con clientes sacados y sin
 * ninguna forma de traerlos de vuelta desde ese paso.
 *
 * Filtra por `dismissedAt` y no por `includeBeforeDismiss`: el sistema también descarta (las cuentas
 * muertas de la plataforma), y ofrecerle al usuario «deshacer» algo que él nunca hizo es peor que no
 * ofrecer nada.
 */
export function dismissedAtOf(groups, at) {
    const byClient = new Map();
    (groups.removed || []).forEach(entry => {
        if (entry.decision?.dismissedAt !== at) return;
        const key = entry.clientName || '';
        if (!byClient.has(key)) byClient.set(key, {key, name: entry.clientName, pks: []});
        byClient.get(key).pks.push(entry.pk);
    });
    return Array.from(byClient.values());
}

/**
 * Las filas del paso 4 con cada deshacer en el LUGAR donde estaba su cliente (H-03).
 *
 * El deshacer vivía al final de la tabla: Pablo sacó un cliente creyendo que sacaba el monto y la
 * confirmación apareció fuera de la vista desde donde hizo el clic (recorrido del 13/10). Los
 * clientes vienen ordenados por nombre (`clientGroupsOf`), así que cada descarte va antes del primer
 * cliente que lo sigue en ese mismo orden: queda donde estaba la fila. Devuelve
 * `{kind: 'group', group}` o `{kind: 'dismissed', entry}`.
 */
export function withDismissedInPlace(groups, dismissed) {
    const pending = (dismissed || []).slice().sort((a, b) => byName(a, b));
    const rows = [];
    (groups || []).forEach(group => {
        while (pending.length && byName(pending[0], group) < 0) {
            rows.push({kind: 'dismissed', entry: pending.shift()});
        }
        rows.push({kind: 'group', group});
    });
    pending.forEach(entry => rows.push({kind: 'dismissed', entry}));
    return rows;
}
